<template>
  <div class="json-list-editor" :class="{ 'has-error': parseError }" :data-json-field="fieldKey">
    <b-table-simple small bordered responsive class="mb-1">
      <b-thead>
        <b-tr>
          <b-th style="width: 90%">{{ valueLabel }}</b-th>
          <b-th style="width: 10%"></b-th>
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr v-for="(item, idx) in items" :key="idx">
          <b-td>
            <b-form-input
              size="sm"
              v-model="items[idx]"
              :placeholder="placeholder"
              @input="emitUpdate"
            />
          </b-td>
          <b-td class="text-center align-middle">
            <b-button size="sm" variant="outline-danger" @click="removeRow(idx)" title="Remove">
              &times;
            </b-button>
          </b-td>
        </b-tr>
        <b-tr v-if="items.length === 0">
          <b-td colspan="2" class="text-center text-muted" style="font-style: italic; font-size: 13px;">
            No entries. Click "Add Row" below.
          </b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>
    <div class="d-flex align-items-center" style="gap: 8px;">
      <b-button size="sm" variant="outline-primary" @click="addRow">+ Add Row</b-button>
      <b-button v-if="allowRawJson" size="sm" variant="outline-secondary" @click="showRaw = !showRaw">
        {{ showRaw ? 'Hide' : 'Show' }} Raw JSON
      </b-button>
      <span v-if="parseError" class="text-danger" style="font-size: 12px;">{{ parseError }}</span>
    </div>
    <b-form-textarea
      v-if="allowRawJson && showRaw"
      :value="value"
      @input="onRawInput"
      rows="2"
      class="mt-2"
      style="font-family: monospace; font-size: 13px;"
    />
  </div>
</template>

<script>
export default {
  name: 'JsonTagListEditor',
  props: {
    value: { type: String, default: '' },
    fieldKey: { type: String, default: '' },
    valueLabel: { type: String, default: 'Value' },
    placeholder: { type: String, default: '' },
    allowRawJson: { type: Boolean, default: false },
  },
  created() {
    this._parseTimer = null
  },
  beforeDestroy() {
    if (this._parseTimer) clearTimeout(this._parseTimer)
  },
  data() {
    return {
      items: [],
      showRaw: false,
      parseError: '',
      internalUpdate: false,
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (this.internalUpdate) {
          this.internalUpdate = false
          return
        }
        this.parseValue(newVal)
      },
    },
    parseError(val) {
      if (val && this.allowRawJson) this.showRaw = true
    },
    allowRawJson(val) {
      if (!val) {
        this.showRaw = false
        if (this._parseTimer) {
          clearTimeout(this._parseTimer)
          this._parseTimer = null
        }
      }
    },
  },
  methods: {
    parseValue(jsonStr) {
      this.parseError = ''
      if (!jsonStr || jsonStr.trim() === '') {
        this.items = []
        this.emitValidation('')
        return
      }
      try {
        const arr = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr
        if (Array.isArray(arr)) {
          this.items = arr.map(String)
          this.emitValidation('')
        } else {
          this.parseError = 'Expected a JSON array'
          this.items = []
          this.emitValidation(this.parseError)
        }
      } catch (e) {
        this.parseError = 'Invalid JSON: ' + e.message
        this.items = []
        this.emitValidation(this.parseError)
      }
    },
    emitUpdate() {
      this.internalUpdate = true
      const filtered = this.items.filter(v => v.trim() !== '')
      const json = filtered.length > 0 ? JSON.stringify(filtered) : ''
      this.$emit('input', json)
    },
    onRawInput(val) {
      this.internalUpdate = true
      this.$emit('input', val)
      if (this._parseTimer) clearTimeout(this._parseTimer)
      this._parseTimer = setTimeout(() => {
        this.parseValue(val)
      }, 1000)
    },
    addRow() {
      this.items.push('')
    },
    removeRow(idx) {
      this.items.splice(idx, 1)
      this.emitUpdate()
    },
    emitValidation(error) {
      if (this.fieldKey) {
        this.$emit('validation', this.fieldKey, error)
      }
    },
  },
}
</script>

<style scoped>
.json-list-editor .table {
  margin-bottom: 4px;
}
.json-list-editor .table td {
  padding: 4px 6px;
  vertical-align: middle;
}
.json-list-editor .table th {
  padding: 4px 6px;
  font-size: 13px;
  font-weight: 600;
  background: #f8f9fa;
}
.json-list-editor.has-error .table {
  border-color: #dc3545;
}
</style>
